import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  chunkForSpeech,
  createRecognition,
  isVoiceInputSupported,
  isVoiceOutputSupported,
  matchWakePhrase,
  pickVoice,
  playChime,
  requestMicrophoneAccess,
  SpeechRecognitionLike,
  stripWakePhrase,
  toSpokenText,
  WAKE_PHRASE_LABEL,
} from './speech'

/**
 * Hands-free voice for the assistant: say "Hey Nexys", ask, hear the answer back.
 *
 * The whole thing is one microphone and one state machine. A second SpeechRecognition instance
 * would be the obvious way to split "waiting for the wake word" from "taking the request", but
 * browsers only hand the mic to one recogniser at a time and the hand-off drops the first second
 * of speech - which is exactly the second holding the question. So there is a single recogniser
 * and a mode flag, and waking mid-utterance just marks where in the transcript the request starts.
 */

export type VoiceState =
  /** No SpeechRecognition in this browser. */
  | 'unsupported'
  /** Supported, but the user has not turned wake listening on and is not holding the mic. */
  | 'off'
  /** Microphone open, waiting for the wake phrase. */
  | 'idle'
  /** Awake and capturing a request. */
  | 'listening'
  /** Request sent, waiting on the assistant. */
  | 'thinking'
  /** Reading the answer out. */
  | 'speaking'
  /** The user (or policy) refused the microphone. */
  | 'denied'

export interface VoiceAssistant {
  /** The browser has a speech recogniser at all. */
  supported: boolean
  /**
   * Voice can actually run right now - supported *and* permitted to hold the microphone. Signed
   * out it is false, and the controls are hidden rather than offering to arm a mic that the state
   * machine will refuse to open.
   */
  available: boolean
  outputSupported: boolean
  state: VoiceState
  /** True whenever the mic is open for any reason - drives the "we are recording" indicator. */
  micLive: boolean
  /**
   * Wake listening is switched on but the microphone is not actually open, because the browser is
   * holding out for a user interaction on this freshly loaded page. The indicator has to show this
   * rather than a confident "listening" - the whole point of the dot is that it can be trusted.
   */
  pendingGesture: boolean
  /**
   * Wake listening is switched on but the microphone is deliberately released because the app's
   * window is not the one in focus. Holding an open capture in the background is what breaks
   * audio in meeting apps - a Bluetooth headset gets pinned in handsfree mode by our capture and
   * Zoom finds the device unusable. Focus the window again and listening resumes on its own.
   */
  suspended: boolean
  wakeEnabled: boolean
  setWakeEnabled: (value: boolean) => void
  speakReplies: boolean
  setSpeakReplies: (value: boolean) => void
  /** What has been heard so far this turn, wake phrase already removed. */
  transcript: string
  /** One short line of status or explanation for the user. Empty when there is nothing to say. */
  notice: string
  /** Push-to-talk. Skips the wake phrase entirely. */
  startListening: () => void
  /** Send what has been heard so far without waiting for the pause. */
  submitNow: () => void
  /** Stop listening, or stop talking - whichever is happening. */
  cancel: () => void
  wakePhrase: string
}

const WAKE_STORAGE_KEY = 'aiAssistantVoiceWake'
const SPEAK_STORAGE_KEY = 'aiAssistantVoiceSpeak'

/** How long a pause ends the request. Long enough to think mid-sentence, short enough to feel live. */
const SILENCE_MS = 1500
/** Woken but nothing said: give up and go back to waiting rather than holding an open mic. */
const NO_SPEECH_MS = 6000
/** Hard ceiling on one request, so a stuck recogniser cannot listen forever. */
const MAX_CAPTURE_MS = 20000
/** Recognition ends itself constantly; this is the pause before restarting it. */
const RESTART_DELAY_MS = 250
/**
 * How long the window has to stay unfocused before the microphone is released. Long enough that
 * a dropdown or a file dialog stealing focus for a beat does not cycle the mic, short enough
 * that by the time a meeting app is doing anything with audio, we have let go.
 */
const BLUR_RELEASE_MS = 1000
/**
 * Chrome stops firing recognition events (usually reporting `network`) if the speech service is
 * unreachable, and an unthrottled restart loop then spins forever. After this many consecutive
 * failures with nothing heard in between, voice backs off for a cooldown and says so.
 */
const MAX_CONSECUTIVE_ERRORS = 5
/**
 * How long that backoff lasts. It used to be the rest of the session: the usual cause is a
 * passing network or audio-device problem, and voice that never comes back on its own is
 * indistinguishable from broken - the user's only clue was to cycle the toggle by hand.
 */
const ERROR_COOLDOWN_MS = 20000
/** How often to check that the microphone we believe is open really is. */
const LIVENESS_CHECK_MS = 2000
/**
 * How long a supposedly-running recogniser may go without producing a single event before it is
 * treated as dead. A live Chrome session is noisy - it gives up on silence and restarts every few
 * seconds - so total quiet for this long means the engine stopped without telling us. It does
 * that: start() succeeds, onstart fires, and then onresult, onerror and onend never come again,
 * which is what left "Hey Nexys" answering once and then going deaf until the toggle was cycled.
 */
const STALE_RUNNING_MS = 30000
/** A start() that has produced neither onstart nor onend by now never will. */
const STALE_STARTING_MS = 5000
/** Rebuilds that produced no sign of life before we stop trying quietly and ask for a click. */
const MAX_SILENT_REVIVALS = 3
/**
 * Chrome wants a beat after it finishes speaking before it will capture audio again. Reopening the
 * microphone in the same tick as the last utterance's end is a good way to have the next wake land
 * on an engine that is still tearing down the output device.
 */
const POST_SPEECH_SETTLE_MS = 400

const clearTimer = (ref: MutableRefObject<number | null>) => {
  if (ref.current !== null) {
    window.clearTimeout(ref.current)
    ref.current = null
  }
}

const readFlag = (key: string, fallback: boolean) => {
  try {
    const stored = localStorage.getItem(key)
    return stored === null ? fallback : stored === '1'
  } catch {
    // Private mode can throw on access; losing the preference is not worth failing over.
    return fallback
  }
}

const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, value ? '1' : '0')
  } catch {
    // See readFlag.
  }
}

interface UseVoiceAssistantOptions {
  /** Voice is only ever live for a signed-in user with the assistant mounted. */
  enabled: boolean
  /** Sends a turn and resolves with the assistant's answer as markdown. */
  sendPrompt: (prompt: string, options: { voice: boolean }) => Promise<string>
  /** Called the moment the wake phrase lands, so the panel can come forward before the question ends. */
  onWake?: () => void
}

export function useVoiceAssistant({ enabled, sendPrompt, onWake }: UseVoiceAssistantOptions): VoiceAssistant {
  const supported = useMemo(() => isVoiceInputSupported(), [])
  const outputSupported = useMemo(() => isVoiceOutputSupported(), [])

  const [state, setState] = useState<VoiceState>(supported ? 'off' : 'unsupported')
  const [wakeEnabled, setWakeEnabledState] = useState(() => supported && readFlag(WAKE_STORAGE_KEY, false))
  const [speakReplies, setSpeakRepliesState] = useState(() => readFlag(SPEAK_STORAGE_KEY, true))
  const [transcript, setTranscript] = useState('')
  const [notice, setNotice] = useState('')
  const [suspended, setSuspendedState] = useState(false)

  // The machine runs off refs. Recognition callbacks are registered once and outlive every render,
  // so anything they read has to be current rather than captured.
  const stateRef = useRef<VoiceState>(state)
  const wakeEnabledRef = useRef(wakeEnabled)
  const speakRepliesRef = useRef(speakReplies)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const runningRef = useRef(false)
  const wantRunningRef = useRef(false)
  const restartTimerRef = useRef<number | null>(null)
  const silenceTimerRef = useRef<number | null>(null)
  const maxCaptureTimerRef = useRef<number | null>(null)
  const errorCountRef = useRef(0)
  /** A one-shot interaction listener is waiting to open the microphone. */
  const gestureArmedRef = useRef(false)
  const armOnNextGestureRef = useRef<() => void>(() => {})
  const gestureAttemptsRef = useRef(0)
  const gestureCleanupRef = useRef<(() => void) | null>(null)
  /** Recognition actually got going at least once on this page load. */
  const hasRunRef = useRef(false)
  /**
   * In-flight utterances, held by reference. Chrome garbage-collects utterance objects nothing
   * references, and a collected utterance's onend never fires - which left the machine stuck in
   * "speaking" with the microphone deliberately closed, so the next wake fell on a dead mic.
   */
  const utterancesRef = useRef<SpeechSynthesisUtterance[]>([])
  const startRecognitionRef = useRef<(force?: boolean) => void>(() => {})
  /** When the recogniser last showed any sign of life - see STALE_RUNNING_MS. */
  const lastEventAtRef = useRef(0)
  /** Consecutive forced rebuilds that produced nothing. Reset by any real recognition event. */
  const revivalsRef = useRef(0)
  /** Restarts are held off until this moment after the error ceiling is hit. */
  const cooldownUntilRef = useRef(0)
  const settleTimerRef = useRef<number | null>(null)
  const suspendedRef = useRef(false)
  const blurTimerRef = useRef<number | null>(null)
  /** Where in the current recognition session the request starts - set when the wake phrase lands. */
  const captureStartIndexRef = useRef(0)
  /** Finalised text carried across a recognition restart mid-request. */
  const committedRef = useRef('')
  const sessionFinalsRef = useRef('')
  /**
   * Exactly what is on screen this turn: finalised text plus whatever is still interim, wake
   * phrase already removed. This, not the finalised text alone, is what gets sent.
   *
   * Chrome does not reliably emit a final result for an utterance - it ends the session, or the
   * speech service simply never promotes the interim - and building the request from finals only
   * meant the user watched their question appear on screen and then be told it was not heard.
   */
  const liveRef = useRef('')
  const heardRef = useRef(false)
  const voicesRef = useRef<SpeechSynthesisVoice[]>([])
  /** Bumped on every turn so a cancelled utterance's callbacks cannot move the next turn along. */
  const speechTokenRef = useRef(0)
  const sendPromptRef = useRef(sendPrompt)
  const onWakeRef = useRef(onWake)

  sendPromptRef.current = sendPrompt
  onWakeRef.current = onWake
  speakRepliesRef.current = speakReplies

  const applyState = useCallback((next: VoiceState) => {
    stateRef.current = next
    setState(next)
  }, [])

  const clearCaptureTimers = useCallback(() => {
    clearTimer(silenceTimerRef)
    clearTimer(maxCaptureTimerRef)
  }, [])

  const resetCapture = useCallback(() => {
    captureStartIndexRef.current = 0
    committedRef.current = ''
    sessionFinalsRef.current = ''
    liveRef.current = ''
    heardRef.current = false
    setTranscript('')
  }, [])

  /* ---------------------------------------------------------------------- */
  /*  Speech synthesis                                                      */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!outputSupported) return
    const load = () => {
      voicesRef.current = window.speechSynthesis.getVoices()
    }
    load()
    // Chrome populates the voice list asynchronously; without this the first answer is read in the
    // platform default voice and every later one in the chosen voice.
    window.speechSynthesis.addEventListener('voiceschanged', load)
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load)
  }, [outputSupported])

  /**
   * Ends a spoken turn and hands the microphone back, but not in the same tick the last utterance
   * finished in. Chrome is still tearing down the output device then, and a recogniser started
   * inside that window is the one that comes up deaf - which is how the assistant answered the
   * first question and then never heard another.
   */
  const settleAfterSpeaking = useCallback(() => {
    clearTimer(settleTimerRef)
    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null
      // Only ever ends the turn it was scheduled for. By now the user may have woken it again,
      // and dropping that back to idle would throw away the question being captured.
      if (stateRef.current !== 'speaking') return
      applyState(wakeEnabledRef.current ? 'idle' : 'off')
    }, POST_SPEECH_SETTLE_MS)
  }, [applyState])

  const stopSpeaking = useCallback(() => {
    clearTimer(settleTimerRef)
    speechTokenRef.current += 1
    utterancesRef.current = []
    if (outputSupported) window.speechSynthesis.cancel()
  }, [outputSupported])

  /* ---------------------------------------------------------------------- */
  /*  Recognition lifecycle                                                 */
  /* ---------------------------------------------------------------------- */

  const stopRecognition = useCallback((hard = false) => {
    wantRunningRef.current = false
    clearTimer(restartTimerRef)
    const recognition = recognitionRef.current
    if (!recognition) return
    try {
      if (hard) recognition.abort()
      else recognition.stop()
    } catch {
      // stop() on an already-stopped recogniser throws in some builds; nothing to do about it.
    }
  }, [])

  /** Drops the current recognition session so the next one starts with an empty transcript. */
  const restartRecognition = useCallback(() => {
    const recognition = recognitionRef.current
    if (!recognition || !runningRef.current) return
    // onend restarts it, because wantRunning stays true.
    try {
      recognition.abort()
    } catch {
      // See stopRecognition.
    }
  }, [])

  const finishCapture = useCallback(() => {
    if (stateRef.current !== 'listening') return
    clearCaptureTimers()
    const heard = (liveRef.current || stripWakePhrase(`${committedRef.current} ${sessionFinalsRef.current}`.trim())).trim()
    resetCapture()
    captureStartIndexRef.current = 0

    if (!heard) {
      playChime('done')
      setNotice(`Didn't catch that. Say "${WAKE_PHRASE_LABEL}" to try again.`)
      applyState(wakeEnabledRef.current ? 'idle' : 'off')
      restartRecognition()
      return
    }

    setNotice('')
    applyState('thinking')
    // A fresh session, so a stale "hey nexys" left in the results array cannot wake it again.
    restartRecognition()

    const token = (speechTokenRef.current += 1)
    sendPromptRef
      .current(heard, { voice: true })
      .then((answer) => {
        if (token !== speechTokenRef.current) return
        const spoken = speakRepliesRef.current ? toSpokenText(answer || '') : ''
        if (!spoken || !outputSupported) {
          applyState(wakeEnabledRef.current ? 'idle' : 'off')
          return
        }
        applyState('speaking')
        // The mic is closed while the answer plays. Leaving it open means the assistant hears
        // itself say "Nexys" and wakes on its own reply; browser echo cancellation is not
        // dependable enough to bet the feature on. Barge-in is the Stop control instead.
        stopRecognition(true)
        const chunks = chunkForSpeech(spoken)
        let remaining = chunks.length
        const finish = () => {
          if (token !== speechTokenRef.current) return
          remaining -= 1
          if (remaining > 0) return
          utterancesRef.current = []
          settleAfterSpeaking()
        }
        window.speechSynthesis.cancel()
        utterancesRef.current = []
        chunks.forEach((chunk) => {
          const utterance = new SpeechSynthesisUtterance(chunk)
          // Held by reference until the turn ends - see utterancesRef.
          utterancesRef.current.push(utterance)
          // Always the automatic pick. A stored aiAssistantVoiceUri from the brief voice-picker
          // era is deliberately ignored - the picker is gone, so a leftover choice with no UI to
          // change it must not keep deciding how the assistant sounds.
          const voice = pickVoice(voicesRef.current)
          if (voice) {
            utterance.voice = voice
            utterance.lang = voice.lang
          }
          utterance.rate = 1.02
          utterance.onend = finish
          utterance.onerror = finish
          window.speechSynthesis.speak(utterance)
        })
      })
      .catch(() => {
        if (token !== speechTokenRef.current) return
        setNotice('That did not go through. Try again.')
        applyState(wakeEnabledRef.current ? 'idle' : 'off')
      })
  }, [
    applyState,
    clearCaptureTimers,
    outputSupported,
    resetCapture,
    restartRecognition,
    settleAfterSpeaking,
    stopRecognition,
  ])

  const finishCaptureRef = useRef(finishCapture)
  finishCaptureRef.current = finishCapture

  const beginCapture = useCallback(() => {
    clearCaptureTimers()
    heardRef.current = false
    committedRef.current = ''
    sessionFinalsRef.current = ''
    liveRef.current = ''
    setTranscript('')
    setNotice('')
    applyState('listening')
    playChime('wake')
    onWakeRef.current?.()
    maxCaptureTimerRef.current = window.setTimeout(() => finishCaptureRef.current(), MAX_CAPTURE_MS)
    silenceTimerRef.current = window.setTimeout(() => finishCaptureRef.current(), NO_SPEECH_MS)
  }, [applyState, clearCaptureTimers])

  const beginCaptureRef = useRef(beginCapture)
  beginCaptureRef.current = beginCapture

  /**
   * The one place a restart is queued. Everything goes through it so the cooldown after a run of
   * errors cannot be sidestepped, and so a pending restart is visible to the watchdog below -
   * `restartTimerRef` is cleared when the timer fires, not just when it is cancelled, or a timer
   * that has already run would look forever like a restart still on its way.
   */
  const scheduleRestart = useCallback((delay = RESTART_DELAY_MS) => {
    if (!wantRunningRef.current) return
    // Nothing is queued at all while cooling off after a run of errors. The liveness watchdog is
    // what brings listening back once the cooldown has passed, and keeping that in one place is
    // what stops the two of them from restarting each other in a loop.
    if (Date.now() < cooldownUntilRef.current) return
    clearTimer(restartTimerRef)
    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null
      if (!wantRunningRef.current || runningRef.current) return
      // Through startRecognition rather than recognition.start(): the restart must also get a
      // fresh instance, or it would resurrect the very corpse being replaced.
      startRecognitionRef.current()
    }, delay)
  }, [])

  const scheduleRestartRef = useRef(scheduleRestart)
  scheduleRestartRef.current = scheduleRestart

  /**
   * Builds a FRESH recogniser and wires its handlers to refs, never to a render's closure.
   *
   * Fresh matters: Chrome lets a reused SpeechRecognition instance go quietly dead after a few
   * abort() cycles - start() succeeds, onstart fires, and onresult never arrives again. Our turn
   * cycle aborts twice per question (on submit, and when the answer starts playing), so the
   * second wake landed on exactly such a corpse. A new instance per session avoids the state
   * entirely, and the handlers below guard against events from a discarded predecessor.
   */
  const buildRecognition = useCallback(() => {
    const recognition = createRecognition()
    if (!recognition) return null

    recognition.onstart = () => {
      if (recognitionRef.current !== recognition) return
      runningRef.current = true
      hasRunRef.current = true
      gestureAttemptsRef.current = 0
      // Deliberately not a revival reset: a dead Chrome recogniser still fires onstart. Only the
      // events that prove the engine is actually working clear that counter.
      lastEventAtRef.current = Date.now()
    }

    recognition.onresult = (event) => {
      if (recognitionRef.current !== recognition) return
      errorCountRef.current = 0
      revivalsRef.current = 0
      lastEventAtRef.current = Date.now()
      const results = event.results

      if (stateRef.current !== 'listening') {
        // Waiting for the wake phrase. Scanning the last two results rather than only the newest
        // covers a phrase that lands across a result boundary.
        const scanFrom = Math.max(0, results.length - 2)
        let combined = ''
        for (let i = scanFrom; i < results.length; i += 1) combined += `${results[i][0].transcript} `
        if (!matchWakePhrase(combined)) return
        captureStartIndexRef.current = scanFrom
        beginCaptureRef.current()
        // Deliberately no return: "Hey Nexys, what's the payoff on 1005" is one utterance, and the
        // question is in the very result that just woke it. Bailing out here waits for a second
        // utterance that never comes, and the turn dies as "didn't catch that".
      }

      let finals = ''
      let interim = ''
      for (let i = captureStartIndexRef.current; i < results.length; i += 1) {
        if (results[i].isFinal) finals += `${results[i][0].transcript} `
        else interim += `${results[i][0].transcript} `
      }
      sessionFinalsRef.current = finals
      const live = stripWakePhrase(`${committedRef.current} ${finals} ${interim}`.trim())
      liveRef.current = live
      setTranscript(live)

      if (live) heardRef.current = true
      clearTimer(silenceTimerRef)
      silenceTimerRef.current = window.setTimeout(
        () => finishCaptureRef.current(),
        heardRef.current ? SILENCE_MS : NO_SPEECH_MS,
      )
    }

    recognition.onerror = (event) => {
      if (recognitionRef.current !== recognition) return
      // An error is a healthy sign of life as far as the watchdog is concerned: the engine is
      // still talking to us. no-speech in particular arrives all day long on an open, quiet mic.
      lastEventAtRef.current = Date.now()
      revivalsRef.current = 0
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        // Chrome reports the same error for "the user blocked the mic" and "this page has not been
        // interacted with yet". Telling them apart matters: the second is not a refusal, and
        // turning the user's preference off for it is how a restored session goes silently deaf.
        if (!hasRunRef.current && gestureAttemptsRef.current < 2) {
          wantRunningRef.current = false
          applyState('off')
          armOnNextGestureRef.current()
          return
        }
        wantRunningRef.current = false
        wakeEnabledRef.current = false
        setWakeEnabledState(false)
        writeFlag(WAKE_STORAGE_KEY, false)
        setNotice('Microphone access is blocked. Allow it in your browser settings to use voice.')
        applyState('denied')
        return
      }
      // no-speech and aborted are ordinary: the recogniser gives up on silence all day long.
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        errorCountRef.current += 1
        if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
          // Back off instead of standing down for the session. A run of errors is nearly always a
          // passing network or audio-device problem, and voice that never comes back on its own
          // reads as broken - the user's only clue was to cycle the toggle by hand.
          errorCountRef.current = 0
          cooldownUntilRef.current = Date.now() + ERROR_COOLDOWN_MS
          setNotice('Speech recognition is unavailable right now. Trying again in a moment.')
        }
      }
    }

    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return
      runningRef.current = false
      lastEventAtRef.current = Date.now()
      revivalsRef.current = 0
      // A restart mid-request loses the results array, so keep what was already finalised.
      if (stateRef.current === 'listening') {
        // Carry the interim words too. The new session starts with an empty results array, so
        // anything not committed here is gone - and mid-sentence is exactly when Chrome ends.
        committedRef.current = liveRef.current
        sessionFinalsRef.current = ''
        captureStartIndexRef.current = 0
      }
      scheduleRestartRef.current()
    }

    recognitionRef.current = recognition
    return recognition
  }, [applyState])

  const startRecognition = useCallback((force = false) => {
    wantRunningRef.current = true
    // `force` is the watchdog replacing a recogniser that claims to be running but has gone
    // silent. Trusting the flag is exactly how the machine got stuck: Chrome can leave an
    // instance marked running for good, and every later start() then returned here doing nothing.
    if (runningRef.current && !force) return
    clearTimer(restartTimerRef)
    // Retire the old instance before building its replacement, so a straggling onend from it
    // cannot re-enter the restart logic.
    const old = recognitionRef.current
    recognitionRef.current = null
    // Its onend is guarded out by the identity check, so nothing else will ever clear this.
    runningRef.current = false
    if (old) {
      try {
        old.abort()
      } catch {
        // Discarded either way.
      }
    }
    const recognition = buildRecognition()
    if (!recognition) return
    lastEventAtRef.current = Date.now()
    try {
      recognition.start()
    } catch {
      // Two different failures land here. If it is already starting, onstart settles it. But
      // Chrome also throws when it will not open the microphone on a freshly loaded page that has
      // had no interaction yet - and in that case NOTHING fires, not even onend, so swallowing
      // this left the panel armed and deaf until the user toggled it off and on by hand.
      if (runningRef.current) return
      // Once the microphone has opened on this page, a throw is a transient clash with the
      // session being torn down, not the browser asking for a gesture. Retrying keeps listening
      // alive; dropping to 'off' here would strand it behind a "click anywhere" prompt instead.
      if (hasRunRef.current) {
        scheduleRestartRef.current(RESTART_DELAY_MS * 4)
        return
      }
      applyState('off')
      armOnNextGestureRef.current()
    }
  }, [applyState, buildRecognition])

  startRecognitionRef.current = startRecognition

  /**
   * Waits for the first click or keypress and opens the microphone then, which is the interaction
   * the browser was holding out for. Bounded by gestureAttemptsRef so a genuinely blocked
   * microphone reports itself as blocked instead of asking for clicks forever.
   */
  const armOnNextGesture = useCallback(() => {
    if (gestureArmedRef.current || !wakeEnabledRef.current) return
    gestureArmedRef.current = true
    gestureAttemptsRef.current += 1
    setNotice('Click anywhere to resume listening.')
    const onGesture = () => {
      gestureArmedRef.current = false
      revivalsRef.current = 0
      cooldownUntilRef.current = 0
      window.removeEventListener('pointerdown', onGesture, true)
      window.removeEventListener('keydown', onGesture, true)
      if (!wakeEnabledRef.current) return
      setNotice('')
      applyState('idle')
      // Started inside the gesture's own task, while the activation the browser wants is fresh.
      startRecognition()
    }
    window.addEventListener('pointerdown', onGesture, true)
    window.addEventListener('keydown', onGesture, true)
    gestureCleanupRef.current = () => {
      gestureArmedRef.current = false
      window.removeEventListener('pointerdown', onGesture, true)
      window.removeEventListener('keydown', onGesture, true)
    }
  }, [applyState, startRecognition])

  armOnNextGestureRef.current = armOnNextGesture

  /**
   * The single place that decides whether the mic should be open. Every transition just sets state
   * and lets this reconcile, which is what keeps "listening while the answer plays" from ever
   * happening by accident.
   */
  useEffect(() => {
    if (!supported) return
    const shouldListen =
      enabled &&
      document.visibilityState === 'visible' &&
      (state === 'listening' || ((state === 'idle' || state === 'thinking') && wakeEnabled))

    if (shouldListen) startRecognition()
    else stopRecognition(state === 'speaking')
  }, [supported, enabled, state, wakeEnabled, startRecognition, stopRecognition])

  /**
   * Liveness. Everything above assumes the browser reports what its recogniser is doing, and the
   * field says otherwise: Chrome can leave a session marked running that never delivers another
   * event, and a start() can return cleanly having fired nothing at all. Either one used to be
   * terminal - the mic indicator kept claiming to listen, and only toggling "Hey Nexys" off and
   * on brought it back, because that is the one path that rebuilds from scratch.
   *
   * So the flags are checked against the clock rather than believed. If we want the microphone
   * open and the engine has produced no event for long enough that a healthy one would have
   * ended and restarted several times over, it is dead: replace it. If replacing it changes
   * nothing a few times running, say so and wait for a click, which is what Chrome wants when it
   * has stopped serving a page's recogniser entirely.
   */
  useEffect(() => {
    if (!supported || !enabled) return
    const timer = window.setInterval(() => {
      // Not wanting the mic is not a fault: it is closed on purpose while the answer plays, while
      // another app has focus, and while a click is awaited.
      if (!wantRunningRef.current || suspendedRef.current || gestureArmedRef.current) return
      if (restartTimerRef.current !== null) return
      const now = Date.now()
      if (now < cooldownUntilRef.current) return
      const quietFor = now - lastEventAtRef.current
      if (quietFor < (runningRef.current ? STALE_RUNNING_MS : STALE_STARTING_MS)) return

      revivalsRef.current += 1
      if (revivalsRef.current > MAX_SILENT_REVIVALS) {
        wantRunningRef.current = false
        stopRecognition(true)
        applyState('off')
        armOnNextGestureRef.current()
        return
      }
      startRecognitionRef.current(true)
    }, LIVENESS_CHECK_MS)
    return () => window.clearInterval(timer)
  }, [supported, enabled, applyState, stopRecognition])

  /**
   * The microphone is held ONLY while this window is the one the user is in.
   *
   * A hidden tab holding an open capture was the obvious case, but an unfocused window is the one
   * that bites: switch to Zoom and the browser window is usually still "visible" on screen, so a
   * visibility check alone leaves our capture running underneath the meeting. An open capture
   * pins a Bluetooth headset in handsfree mode and can make the mic unusable for the app the
   * user is actually talking in. So: blur releases the microphone (after a short grace, since
   * dropdowns and file dialogs steal focus for a beat), focus takes it back, and the wake
   * preference itself is never touched.
   */
  useEffect(() => {
    if (!supported) return

    const release = () => {
      clearTimer(blurTimerRef)
      stopSpeaking()
      clearCaptureTimers()
      stopRecognition(true)
      if (stateRef.current === 'listening' || stateRef.current === 'speaking') resetCapture()
      if (wakeEnabledRef.current) {
        suspendedRef.current = true
        setSuspendedState(true)
      }
      if (stateRef.current !== 'off' && stateRef.current !== 'denied' && stateRef.current !== 'unsupported') {
        applyState('off')
      }
    }

    const resume = () => {
      clearTimer(blurTimerRef)
      if (!suspendedRef.current) return
      suspendedRef.current = false
      revivalsRef.current = 0
      setSuspendedState(false)
      if (wakeEnabledRef.current && stateRef.current === 'off') applyState('idle')
    }

    const onBlur = () => {
      if (blurTimerRef.current !== null) return
      blurTimerRef.current = window.setTimeout(() => {
        blurTimerRef.current = null
        // Focus can come back before the grace runs out (dropdowns, dialogs). Only a still-
        // unfocused window releases the microphone.
        if (!document.hasFocus()) release()
      }, BLUR_RELEASE_MS)
    }

    const onFocus = () => resume()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (document.hasFocus()) resume()
      } else {
        // A hidden tab releases immediately - there is no dialog-flap ambiguity to wait out.
        release()
      }
    }

    // A page that loads into the background (restored session behind another app) must not grab
    // the microphone before the user is even looking at it.
    if (!document.hasFocus() && wakeEnabledRef.current) {
      suspendedRef.current = true
      setSuspendedState(true)
    }

    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearTimer(blurTimerRef)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [supported, applyState, clearCaptureTimers, resetCapture, stopRecognition, stopSpeaking])

  /** A wake preference kept from the last session should still be listening after a reload. */
  useEffect(() => {
    if (!supported || !enabled) return
    if (wakeEnabled && stateRef.current === 'off' && !suspendedRef.current) applyState('idle')
  }, [supported, enabled, wakeEnabled, applyState])

  /** Status lines are momentary. Left up, they read as the assistant's current state forever. */
  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(''), 6000)
    return () => window.clearTimeout(timer)
  }, [notice])

  /**
   * Chrome pauses synthesis after roughly fifteen seconds unless something pokes it. Sentence-sized
   * chunks make this rare, but a long answer still runs past it.
   */
  useEffect(() => {
    if (state !== 'speaking' || !outputSupported) return
    const timer = window.setInterval(() => {
      if (stateRef.current !== 'speaking') return
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        window.speechSynthesis.pause()
        window.speechSynthesis.resume()
      }
    }, 10000)
    return () => window.clearInterval(timer)
  }, [state, outputSupported])

  /**
   * Watchdog for the speaking state itself. Even with references held, Chrome sometimes just
   * never fires an utterance's end event - and "speaking" is the one state that deliberately
   * closes the microphone, so getting stranded there makes every later wake fall on a dead mic.
   * If the synthesiser reports quiet on two consecutive checks while we still think it is
   * talking, the turn is over whether Chrome said so or not.
   */
  useEffect(() => {
    if (state !== 'speaking' || !outputSupported) return
    let quietChecks = 0
    const timer = window.setInterval(() => {
      if (stateRef.current !== 'speaking') return
      const synth = window.speechSynthesis
      if (synth.speaking || synth.pending) {
        quietChecks = 0
        return
      }
      quietChecks += 1
      if (quietChecks < 2) return
      speechTokenRef.current += 1
      utterancesRef.current = []
      settleAfterSpeaking()
    }, 750)
    return () => window.clearInterval(timer)
  }, [state, outputSupported, settleAfterSpeaking])

  // Signing out, or unmounting, must not leave the microphone open.
  useEffect(() => {
    if (enabled) return
    stopSpeaking()
    clearCaptureTimers()
    stopRecognition(true)
    resetCapture()
    applyState(supported ? 'off' : 'unsupported')
  }, [enabled, supported, applyState, clearCaptureTimers, resetCapture, stopRecognition, stopSpeaking])

  useEffect(
    () => () => {
      wantRunningRef.current = false
      gestureCleanupRef.current?.()
      clearTimer(restartTimerRef)
      clearTimer(silenceTimerRef)
      clearTimer(maxCaptureTimerRef)
      clearTimer(settleTimerRef)
      try {
        recognitionRef.current?.abort()
      } catch {
        // Unmounting; nothing left to recover.
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel()
    },
    [],
  )

  /* ---------------------------------------------------------------------- */
  /*  Controls                                                              */
  /* ---------------------------------------------------------------------- */

  const available = supported && enabled

  const setWakeEnabled = useCallback(
    (value: boolean) => {
      if (!available) return
      writeFlag(WAKE_STORAGE_KEY, value)
      wakeEnabledRef.current = value
      setWakeEnabledState(value)
      if (!value) {
        gestureCleanupRef.current?.()
        suspendedRef.current = false
        setSuspendedState(false)
        setNotice('')
        clearCaptureTimers()
        resetCapture()
        stopSpeaking()
        applyState('off')
        return
      }
      revivalsRef.current = 0
      errorCountRef.current = 0
      cooldownUntilRef.current = 0
      // Asking here ties the browser prompt to the click that turned it on, instead of surfacing a
      // bare `not-allowed` from the recogniser some seconds later.
      requestMicrophoneAccess().then((granted) => {
        if (!granted) {
          writeFlag(WAKE_STORAGE_KEY, false)
          wakeEnabledRef.current = false
          setWakeEnabledState(false)
          setNotice('Microphone access is blocked. Allow it in your browser settings to use voice.')
          applyState('denied')
          return
        }
        setNotice(`Listening for "${WAKE_PHRASE_LABEL}".`)
        applyState('idle')
      })
    },
    [available, applyState, clearCaptureTimers, resetCapture, stopSpeaking],
  )

  const setSpeakReplies = useCallback(
    (value: boolean) => {
      writeFlag(SPEAK_STORAGE_KEY, value)
      setSpeakRepliesState(value)
      if (!value) stopSpeaking()
    },
    [stopSpeaking],
  )

  const startListening = useCallback(() => {
    if (!available) return
    if (stateRef.current === 'listening') {
      finishCaptureRef.current()
      return
    }
    stopSpeaking()
    requestMicrophoneAccess().then((granted) => {
      if (!granted) {
        setNotice('Microphone access is blocked. Allow it in your browser settings to use voice.')
        applyState('denied')
        return
      }
      // A wake session already running holds earlier speech in its results array, so the session
      // is dropped and capture starts clean from the first word after the click.
      restartRecognition()
      captureStartIndexRef.current = 0
      beginCaptureRef.current()
    })
  }, [available, applyState, restartRecognition, stopSpeaking])

  const submitNow = useCallback(() => finishCaptureRef.current(), [])

  const cancel = useCallback(() => {
    clearCaptureTimers()
    stopSpeaking()
    resetCapture()
    setNotice('')
    applyState(wakeEnabledRef.current ? 'idle' : 'off')
  }, [applyState, clearCaptureTimers, resetCapture, stopSpeaking])

  return {
    supported,
    available,
    outputSupported,
    state,
    micLive: state === 'listening' || state === 'idle',
    pendingGesture: wakeEnabled && state === 'off' && !suspended,
    suspended: wakeEnabled && suspended,
    wakeEnabled,
    setWakeEnabled,
    speakReplies,
    setSpeakReplies,
    transcript,
    notice,
    startListening,
    submitNow,
    cancel,
    wakePhrase: WAKE_PHRASE_LABEL,
  }
}
